import { computed, type ComputedRef } from 'vue';
import { useUserStore } from '@store/user';

/** The tenant roles VesselPolicy admits to writing a vessel. */
const FLEET_WRITE_ROLES = ['OWNER', 'ADMIN', 'MANAGER', 'OPS'];

/** The platform roles the API's Gate::before grants, via User::isMoveproPlatformUser(). */
const MOVEPRO_PLATFORM_ROLES = ['SUPER', 'MOVEPRO_ADMIN', 'DEVELOPER'];

interface KebabItem {
    type?: string;
    label?: string;
    route?: string;
}

interface TableColumn {
    field?: string;
    kebab?: KebabItem[];
    [key: string]: unknown;
}

/**
 * Whether the current user may create, edit, delete or restore a container or trailer.
 *
 * Mirrors `VesselPolicy` on the API, which admits MANAGER, OPS, ADMIN, OWNER and SUPER.
 *
 * **Presentation only.** Every vessel write endpoint refuses a role outside that set
 * whether or not the CRM renders the control, and the API is what actually protects them.
 * This exists so a refused user is not offered a button that can only fail.
 *
 * The platform branch checks the Movepro company **and** a platform role, because
 * `isMoveproSuperAdminUser` alone is only `company_id === 1000` — any Movepro employee,
 * whatever their role. `hasAnyRole()` reads the roles the API scoped to the tenant being
 * administered, and a platform user's SUPER usually sits on the Movepro tenant alone, so
 * inside a customer tenant that list is empty and the controls would be hidden from exactly
 * the people the API grants. The role scan runs across every tenant to cover that, mirroring
 * `useCanBulkImport` — which ANDs the two halves, and is the precedent this follows.
 *
 * A platform user on a tenant with support access turned off is denied by the API's
 * `Gate::before` regardless, so these controls will 403 there. That is not special to fleet:
 * the same holds for every gated screen on such a tenant, so it is not corrected here.
 *
 * Note `hasAnyRole(roles)` defaults its second argument to true, which returns true for any
 * tenant OWNER or ADMIN whatever was asked for. That is harmless here because both are in
 * the list anyway.
 */
export function isMoveproPlatformUser(userStore: {
    isMoveproSuperAdminUser?: boolean;
    data?: { tenants?: { roles?: string[] }[] } | null;
}): boolean {
    if (!userStore.isMoveproSuperAdminUser) {
        return false;
    }

    return (userStore.data?.tenants ?? []).some((tenant) =>
        (tenant.roles ?? []).some((role) => MOVEPRO_PLATFORM_ROLES.includes(role)),
    );
}

export function useFleetWriteAccess(): ComputedRef<boolean> {
    const userStore = useUserStore();

    return computed(() => isMoveproPlatformUser(userStore) || userStore.hasAnyRole(FLEET_WRITE_ROLES));
}

/**
 * Strip the write affordances out of a fleet index's column set.
 *
 * Shared by the container and trailer indexes so the two cannot drift. Returns the columns
 * untouched for a user who may write.
 *
 * The **Off/on** switch goes because it PATCHes the vessel and the cell module has no
 * read-only mode; whether a record is active stays reachable through the Status filter.
 *
 * The kebab's **Delete** goes, which is the same entry that reads Restore on a deleted row,
 * and so does **Edit**: a user who cannot save should not be offered the form. The list
 * already carries name, type, capacity, contents, fixed-to, depots, transports, trailers,
 * location, next commitment, ownership, provider and active state, so the only things the
 * form adds are the lease and borrow provider details. Losing those is a far smaller cost
 * than a form that looks editable and refuses to save.
 *
 * Note that no other index in this app gates its kebab Edit — the house pattern is to show
 * it and let the API refuse. That refusal navigates the whole CRM to the 403 page rather
 * than surfacing inline, which is a pre-existing platform behaviour and not this change's
 * to fix. Withholding the control is what keeps a refused user away from it.
 *
 * Where stripping empties a kebab entirely the whole column is dropped, because
 * ReportOptions always renders its three-dot trigger and an empty options array would leave
 * a button that opens a blank menu.
 */
export function withoutFleetWriteAffordances<T extends TableColumn>(columns: T[]): T[] {
    return columns
        .filter((column) => column.field !== 'active')
        .map((column) =>
            // `kebab` may be a function as well as an array (MuvalTableRow supports both).
            // Leave a function form alone rather than calling .filter on it and blanking the table.
            Array.isArray(column.kebab) ?
                {
                    ...column,
                    kebab: column.kebab.filter((item) => item.type !== 'delete' && item.type !== 'edit'),
                }
            :   column,
        )
        .filter((column) => !(Array.isArray(column.kebab) && column.kebab.length === 0));
}
